import React from 'react';
import { X, Shield } from 'lucide-react';

interface DisclaimerModalProps {
  onClose: () => void;
}

interface Section {
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    title: 'Research Use Only',
    content: (
      <div className="space-y-3">
        <p>
          RNAverse is a research and informational tool intended solely for research,
          informational, and educational purposes. Nothing on this platform constitutes
          medical advice, clinical guidance, diagnostic information, or a recommendation
          for any therapeutic intervention. RNAverse is not a medical device and has not
          been reviewed or approved by the U.S. Food and Drug Administration or any other
          regulatory authority.
        </p>
        <p>
          Do not use RNAverse outputs to inform clinical decisions, patient care, or any
          clinical, regulatory, or commercial decision-making without independent
          experimental validation by qualified professionals.
        </p>
      </div>
    ),
  },
  {
    title: 'No Warranty',
    content: (
      <p>
        RNAverse and all content generated by the platform are provided &ldquo;as is&rdquo;
        without warranty of any kind, express or implied. LincSwitch Therapeutics makes
        no representations or warranties, including any implied ranking, prioritization, or
        confidence scoring, regarding the accuracy, completeness, or fitness for any particular
        purpose of any data, analysis, or output generated by RNAverse. Computational
        predictions are inherently probabilistic and require independent validation before any
        downstream use.
      </p>
    ),
  },
  {
    title: 'No Reliance',
    content: (
      <p>
        Users may not rely on RNAverse outputs for any clinical, regulatory, or commercial
        decisions. All use is at the user&rsquo;s sole risk, and users assume full
        responsibility for any decisions or actions taken based on RNAverse outputs.
      </p>
    ),
  },
  {
    title: 'Data Sources & Attributions',
    content: (
      <div className="space-y-4">
        <p>
          RNAverse is built on publicly available datasets and open-source tools. All
          precomputed outputs represent derived statistical summaries, including pseudo-bulk
          aggregations, expression statistics, and gene regulatory network scores, and do not
          constitute redistribution of raw underlying datasets.
        </p>

        <div className="space-y-3 pl-4 border-l-2 border-gray-700">
          <div>
            <p className="font-semibold text-gray-200">CZ CELLxGENE Census</p>
            <p>
              Chan Zuckerberg Initiative. RNAverse was built using single-cell RNA-seq data
              downloaded from the CZ CELLxGENE Census, provided by the Chan Zuckerberg
              Initiative. This data is used under the Creative Commons Attribution 4.0
              International License (CC BY 4.0). Attribution: Chan Zuckerberg Initiative. CZ
              CELLxGENE Census. cellxgene.cziscience.com.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-200">scGPT</p>
            <p>
              RNAverse incorporates scGPT, a single-cell foundation model developed by the Bo
              Wang Lab at the University of Toronto. scGPT is used under the MIT License. The
              scGPT model fine-tuning performed for RNAverse used only the CZ CELLxGENE Census
              dataset described above. RNAverse is designed to use only publicly available
              datasets. To the best of our knowledge, no patient-identifiable data was used in
              model training or fine-tuning. Copyright &copy; 2023 Bo Wang Lab, University of
              Toronto.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-200">GENCODE</p>
            <p>
              RNAverse uses GENCODE v44 gene annotation data produced by the GENCODE project at
              EMBL-EBI. GENCODE data is freely available for academic and commercial use.
              Attribution: Frankish A. et al., GENCODE 2023, Nucleic Acids Research.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-200">Ensembl</p>
            <p>
              Gene and transcript reference data sourced from Ensembl (EMBL-EBI). Used under
              Ensembl&rsquo;s open access terms. Attribution: Martin FJ et al., Ensembl 2023,
              Nucleic Acids Research.
            </p>
          </div>

          <div>
            <p className="font-semibold text-gray-200">NCBI Gene &amp; PubMed/PMC</p>
            <p>
              Gene annotation and literature data sourced from NCBI Gene and PubMed/PMC,
              provided by the National Center for Biotechnology Information, U.S. National
              Library of Medicine. Used under NCBI&rsquo;s open access terms.
            </p>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: 'Third-Party Licenses',
    content: (
      <p>
        Each data source and tool integrated into RNAverse is governed by its own terms of use
        and licensing conditions. LincSwitch Therapeutics is not responsible for the
        accuracy, availability, or completeness of third-party data. Users are responsible for
        reviewing and complying with the terms of any underlying data source before using
        RNAverse outputs in downstream work.
      </p>
    ),
  },
  {
    title: 'Privacy',
    content: (
      <p>
        RNAverse does not intentionally collect, store, or process personal health information,
        patient data, or any personally identifiable information. Query inputs are used solely
        to generate platform responses and are not retained for model training or commercial
        purposes. Standard anonymized usage analytics may be collected to monitor platform
        performance and improve user experience.
      </p>
    ),
  },
  {
    title: 'Acceptable Use',
    content: (
      <p>
        RNAverse is provided free of charge for academic and research use. Users may not use
        RNAverse outputs for commercial drug development, commercial licensing, or
        revenue-generating activities without a separate written agreement with LincSwitch
        Therapeutics, Inc. Contact{' '}
        <a href="mailto:info@lincswitch.com" className="text-indigo-400 hover:text-indigo-300 underline">
          info@lincswitch.com
        </a>{' '}
        for commercial licensing inquiries.
      </p>
    ),
  },
  {
    title: 'Limitation of Liability',
    content: (
      <p>
        To the fullest extent permitted by applicable law, LincSwitch Therapeutics and
        its founders, officers, employees, and collaborators shall not be liable for any
        direct, indirect, incidental, consequential, or punitive damages arising from use of
        or reliance on RNAverse or its outputs.
      </p>
    ),
  },
  {
    title: 'Right to Modify / Terminate',
    content: (
      <p>
        We reserve the right to modify or discontinue RNAverse at any time without notice.
        Continued use of RNAverse following any modification constitutes acceptance of the
        revised terms.
      </p>
    ),
  },
  {
    title: 'Intellectual Property',
    content: (
      <p>
        RNAverse, its underlying methodology, precomputed datasets, software, and brand are
        the intellectual property of LincSwitch Therapeutics Unauthorized reproduction,
        distribution, or commercial use is prohibited without prior written consent.
      </p>
    ),
  },
  {
    title: 'Indemnification',
    content: (
      <p>
        Users agree to indemnify, defend, and hold harmless LincSwitch Therapeutics, its
        officers, directors, employees, and affiliates from and against any claims, damages,
        liabilities, costs, or expenses arising from use or misuse of RNAverse or violation of
        these terms.
      </p>
    ),
  },
  {
    title: 'Governing Law',
    content: (
      <p>
        These terms are governed by the laws of the State of Delaware, USA, without regard to
        conflict of law principles. Any disputes arising under these terms shall be resolved
        exclusively in the state or federal courts located in Delaware. LincSwitch
        Therapeutics, Inc. maintains its principal place of business at 245 First Street, 18th
        Floor, Cambridge, MA 02142.
      </p>
    ),
  },
  {
    title: 'Contact',
    content: (
      <p>
        For questions regarding these terms, contact:{' '}
        <a href="mailto:info@lincswitch.com" className="text-indigo-400 hover:text-indigo-300 underline">
          info@lincswitch.com
        </a>
      </p>
    ),
  },
];

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl bg-gray-900">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 px-8 py-6 rounded-t-2xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/15 rounded-xl">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Legal Disclaimer &amp; Attributions</h2>
              <p className="text-gray-300 text-sm mt-0.5">Last updated: April 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 space-y-6">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider mb-2">
                {section.title}
              </h3>
              <div className="text-sm text-gray-400 leading-relaxed">
                {section.content}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="border-t border-gray-700 pt-4">
            <p className="text-xs text-gray-500 text-center">
              LincSwitch Therapeutics
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
